import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { authApi } from "../../api/authApi";
import Cybermeru from "../../assets/images/merubot_logo.png";
import { useAuth } from "../../contexts/AuthContext";

const AuthCallback = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { setAuthData } = useAuth();

  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "error"
  const [message, setMessage] = useState("Verifying your login...");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);

        const access   = params.get("access");
        const refresh  = params.get("refresh");
        const userType = params.get("user_type");

        // ── GUARD: missing params ──
        if (!access || !refresh || !userType) {
          setStatus("error");
          setMessage("Invalid or incomplete login link. Please try again.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        // ── Call backend to exchange URL tokens → HttpOnly cookies ──
        // This sets proper secure cookies on THIS tenant domain
        const response = await authApi.setTokens({ access, refresh });

        const responseStatus = response?.status;

        if (responseStatus !== 200) {
          setStatus("error");
          setMessage("Authentication failed. Redirecting to login...");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        // ── Clean tokens out of URL immediately (security) ──
        window.history.replaceState({}, document.title, "/auth/callback");

        // ── Store user type ──
        const normalizedUserType = userType === "freelanceer" ? "freelancer" : userType;
        setAuthData({ isAuthenticated: true, user_type: normalizedUserType });

        setStatus("success");
        setMessage("Login successful! Redirecting...");

        // ── Navigate to dashboard ──
        const routeMap = {
          user: "/user/landing",
          recruiter: "/recruiter/dashboard",
          admin: "/admin/dashboard",
          freelancer: "/recruiter/dashboard",
          superadmin: "/tenant/management",
        };

        setTimeout(() => {
          navigate(routeMap[normalizedUserType] || "/recruiter/dashboard");
        }, 800);

      } catch (err) {
        console.error("AuthCallback error:", err);
        setStatus("error");
        setMessage("Something went wrong. Redirecting to login...");
        setTimeout(() => navigate("/login"), 3000);
      }
    };

    handleCallback();
  }, []);

  /* ── UI ── */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.background.grad,
        gap: 3,
      }}
    >
      {/* Logo */}
      <Box
        component="img"
        src={Cybermeru}
        alt="CyberMeru"
        sx={{ height: 60 }}
      />

      {/* Spinner or icon */}
      {status === "verifying" && (
        <CircularProgress
          size={40}
          sx={{ color: theme.palette.primary.main }}
        />
      )}

      {status === "success" && (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            backgroundColor: theme.palette.success.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            color: "#fff",
          }}
        >
          ✓
        </Box>
      )}

      {status === "error" && (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            backgroundColor: theme.palette.error.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            color: "#fff",
          }}
        >
          ✕
        </Box>
      )}

      {/* Message */}
      <Typography
        sx={{
          fontSize: "0.95rem",
          color: theme.palette.text.secondary,
          fontWeight: 500,
          textAlign: "center",
          px: 3,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

export default AuthCallback;